import CollapseCategories16 from '@carbon/icons-react/es/collapse-categories/16';
import ExpandCategories16 from '@carbon/icons-react/es/expand-categories/16';
import Export16 from '@carbon/icons-react/es/export/16';
import { ColumnApi } from 'ag-grid-community';
import { useBoolean } from 'ahooks';
import { InputProps, Tooltip } from 'antd';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { OrderType } from 'api/models';
import { ViewApI } from 'api/views';
import { ViewType } from 'api/views/types';
import { Button } from 'components/atoms/button';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { SearchInput } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { FilterBar } from 'components/molecules/filter-bar';
import { Filter } from 'components/molecules/filters/types';
import { toastService } from 'components/molecules/toast';
import {
  FilterPopover,
  getViewTranformer,
  SavedViews as ViewsComponent,
  Views,
} from 'components/molecules/views';
import { empty, load, loading } from 'components/molecules/views/core';
import { useCurrentViewWithLocalStorage } from 'components/molecules/views/current-view-state';
import { useViewActions } from 'components/molecules/views/view-actions';
import { UsersGroupedByCompanyComponents } from 'components/organisms/filter-editors';
import {
  ColumnListPopover,
  ColumnOrder,
} from 'components/organisms/grid-custom-column';
import { UserEditorComponents } from 'components/organisms/tasks/criteria/editor-components';
import { FilterableField } from 'components/organisms/tasks/fields';
import { GetSelectEditorComponents } from 'components/organisms/tasks/filter-box/utils';
import { ToggleState } from 'components/organisms/templates/projects/plan/table/actions';
import { GptPage } from 'components/pages/rocketlane-gpt/gpt-page';
import { PROJECT_LIST_VIEW_QUERY_KEY } from 'constant';
import { PrereleaseFeature, useFeature } from 'hooks/use-feature';
import {
  addUpdateColumnOrderIndexedDb,
  deleteColumnOrderFromIndexedDb,
  getColumnOrderFromIndexedDb,
} from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { queueMacrotask } from 'utils/macrotask';
import { useProjectFieldsAccess } from 'utils/project-fields/use-project-native-fields-as-filterable-fields';
import { sortByText } from 'utils/sort-by-text';
import { PastProjectStatusField } from '../fields';
import { exportAsCSV } from './export';
import { GroupByField, isAllowedForGroupBy, NoneField } from './group-by';
import { GroupByMenu } from './menu';
import { ProjectCreateButton } from './project-create';
import { getDefaultProjectView } from './view';
import {
  ApiProjectListViewDefinition,
  getViewDefinitionTransformer,
  isValidViewDefinition,
  ProjectListViewDefinition,
  ProjectViews,
  viewDefinitionComparator,
} from './views';

const getSelectEditorComponents: GetSelectEditorComponents = (field) => {
  if (field.fieldName === 'customer') {
    return UserEditorComponents;
  }

  if (field.fieldName === 'teamMembers') {
    return UsersGroupedByCompanyComponents;
  }

  return undefined;
};

const StyledContainer = styled.div`
  padding-left: var(--spacing-02);
  padding-right: var(--spacing-02);
`;

interface ProjectListFilterBarProps {
  selectFilter: (next: Filter) => void;
  clearFilters: () => void;
  filter: Filter;
  searchValue: string;
  onSearchInputChange: InputProps['onChange'];
  allFields: FilterableField[];
  columnFields: FilterableField[];
  groupKey: string;
  setGroupKey: (latest: string) => void;
  columnOrder: ColumnOrder[];
  defaultColumnOrder: ColumnOrder[];
  handleColumnOrderChange: (columnOrder: ColumnOrder[]) => void;
  mode: ToggleState;
  onCollapseChange: (e) => void;
  gridColumnApi?: ColumnApi;
  masterColumnOrder?: ColumnOrder[];
}

interface HasFieldLabel {
  fieldLabel?: string;
}
const byFieldLabel = (
  { fieldLabel: fieldLabelA }: HasFieldLabel,
  { fieldLabel: fieldLabelB }: HasFieldLabel,
) => sortByText(fieldLabelA, fieldLabelB);

const FilterBarSecondary = styled(FilterBar.Secondary)`
  display: flex;
  gap: var(--spacing-04);
`;
const ButtonWrapper = styled.div`
  display: flex;
  flex: 1;
`;
const StyledRadioGroup = styled(RadioGroup)`
  grid-template-columns: repeat(1, minmax(0, 1fr));
  .ant-radio-button-wrapper {
    svg {
      margin-right: 0;
    }
  }
`;

const GptWrapper = styled.div`
  margin-left: var(--spacing-04);
`;

const FilterBarPrimary = styled(FilterBar.Primary)`
  flex-grow: 2 !important;
`;

const excludedProjectFieldsForFilter = ['estimatedDueDate'];

export const ProjectListFilterBar: FC<ProjectListFilterBarProps> = ({
  selectFilter,
  clearFilters,
  filter,
  searchValue,
  onSearchInputChange,
  allFields,
  columnFields,
  groupKey,
  setGroupKey,
  columnOrder = [],
  defaultColumnOrder = [],
  handleColumnOrderChange,
  onCollapseChange,
  mode,
  gridColumnApi,
  masterColumnOrder = [],
}) => {
  const hasAccessToField = useProjectFieldsAccess();
  const fields = useMemo((): FilterableField[] => {
    return allFields.filter(
      (x) =>
        !excludedProjectFieldsForFilter.includes(x.fieldName) &&
        hasAccessToField(x),
    );
  }, [allFields, hasAccessToField]);

  const fieldsForGroupBy = useMemo((): GroupByField[] => {
    return [
      NoneField,
      ...([PastProjectStatusField, ...fields]
        .filter(isAllowedForGroupBy)
        .sort(byFieldLabel) as GroupByField[]),
    ];
  }, [fields]);

  const fields_lookup_by_name = useMemo(
    (): Record<string, FilterableField> =>
      keyBy([...fields, ...fieldsForGroupBy], 'fieldName'),
    [fields, fieldsForGroupBy],
  );

  const fieldsLookupByNameForColumns = useMemo(
    (): Record<string, FilterableField> => keyBy(columnFields, 'fieldName'),
    [columnFields],
  );

  const viewDefinitionTransformer = useMemo(
    () =>
      getViewDefinitionTransformer(fields_lookup_by_name, defaultColumnOrder),
    [defaultColumnOrder, fields_lookup_by_name],
  );
  const defaultViewId = ProjectViews.AllProjects;
  const {
    user: { userId },
  } = useUser();
  const defaultViewsList = useMemo(() => {
    return load(
      getDefaultProjectView(fields_lookup_by_name, userId, defaultColumnOrder),
    );
  }, [defaultColumnOrder, fields_lookup_by_name, userId]);
  const [projectListViews, setProjectListViews] =
    useState<Views<ProjectListViewDefinition>>(empty);

  const viewTransformer = useMemo(
    () => getViewTranformer(viewDefinitionTransformer),
    [viewDefinitionTransformer],
  );
  const { data: views } = useQuery(
    PROJECT_LIST_VIEW_QUERY_KEY,
    () => ViewApI.getProjectListViews<ApiProjectListViewDefinition>(),
    {
      onError: () => {
        setProjectListViews(empty);
      },
    },
  );

  const canShowGpt = useFeature(PrereleaseFeature.Gpt);

  useEffect(() => {
    if (!views) {
      setProjectListViews(loading());

      return;
    }
    setProjectListViews(
      load(
        views
          .filter((v) => v.viewType === ViewType.ProjectList)
          .map(viewTransformer.from),
      ),
    );
  }, [viewTransformer.from, views, fields_lookup_by_name]);

  useEffect(() => {
    async function purgeIndexedDbOfDeletedViews() {
      await deleteColumnOrderFromIndexedDb(
        OrderType.ProjectList,
        projectListViews,
      );
    }
    purgeIndexedDbOfDeletedViews();
  }, [projectListViews]);

  const { onViewCreate, onViewUpdate, onViewDelete } = useViewActions({
    views: projectListViews,
    setViews: setProjectListViews,
    viewType: ViewType.ProjectList,
    transformer: viewDefinitionTransformer,
  });

  const { viewId: currentViewId, setViewId: setCurrentViewId } =
    useCurrentViewWithLocalStorage({
      defaultViewId,
      loading: projectListViews.ids[0] === 'loading',
      localStorageKey: 'project_list.view_id',
    });

  const onViewSelect = useCallback(
    async ({ viewId, viewDefinition }, isClearAll) => {
      const nextFilter = (viewDefinition as ProjectListViewDefinition).filter;

      if (nextFilter) {
        selectFilter(nextFilter);
      } else {
        clearFilters();
      }

      const lsOrder = JSON.parse(
        localStorage.getItem(`project_list.v3.viewId=${viewId}.column_state`) ||
          '[]',
      );

      if (lsOrder && lsOrder.length > 0) {
        await addUpdateColumnOrderIndexedDb(
          `project_list.v1.view=${viewId}`,
          viewId,
          lsOrder,
          OrderType.ProjectList,
        );
        localStorage.removeItem(
          `project_list.v3.viewId=${viewId}.column_state`,
        );
      }
      const data = await getColumnOrderFromIndexedDb(
        `project_list.v1.view=${viewId}`,
      );

      if (defaultViewsList.ids[1]?.includes(viewId)) {
        if (isClearAll) {
          handleColumnOrderChange?.(defaultColumnOrder);
        } else {
          handleColumnOrderChange?.(
            data && data.columnOrder.length > 0
              ? data.columnOrder
              : masterColumnOrder ?? defaultColumnOrder,
          );
        }
      } else if (isClearAll) {
        handleColumnOrderChange?.(
          viewDefinition.columnOrder ?? defaultColumnOrder,
        );
      } else {
        handleColumnOrderChange?.(
          data && data.columnOrder.length > 0
            ? data.columnOrder
            : viewDefinition.columnOrder ??
                masterColumnOrder ??
                defaultColumnOrder,
        );
      }

      setGroupKey(viewDefinition.groupBy);
      queueMacrotask(() => setCurrentViewId(viewId, true));
    },
    [
      clearFilters,
      defaultColumnOrder,
      defaultViewsList.ids,
      handleColumnOrderChange,
      masterColumnOrder,
      selectFilter,
      setCurrentViewId,
      setGroupKey,
    ],
  );

  const [
    isExportInProgress,
    { setTrue: setExportStarted, setFalse: setExportEnded },
  ] = useBoolean(false);
  const translate = useTranslate();

  const exportAsCsvWithToast = () => {
    setExportStarted();
    exportAsCSV(filter, fieldsLookupByNameForColumns, columnOrder)
      .then(() => {
        toastService({
          title: translate('project.exports.sucess.title'),
          message: translate('project.exports.sucess.message'),
        }).success();
      })
      .catch(() => {
        toastService({
          title: translate('project.exports.error.title'),
          message: translate('project.exports.error.message'),
        }).error();
      })
      .finally(() => setExportEnded());
  };

  const expandAllTooltipText = translate('expand_all_projects');
  const collapseAllTooltipText = translate('collapse_all_projects');

  return (
    <FilterBar>
      <FilterBarPrimary data-cy="project_list.actions.bar">
        <ViewsComponent
          filterComponent={
            <FilterPopover
              filter={filter}
              fields={fields}
              onChange={selectFilter}
              loading={false}
              getSelectEditorComponents={getSelectEditorComponents}
            />
          }
          views={projectListViews}
          defaultViewId={defaultViewId}
          defaultViewsList={defaultViewsList}
          currentViewDefinition={{
            filter,
            groupBy: groupKey,
            columnOrder,
          }}
          onViewSelect={onViewSelect}
          isValidViewDefinition={isValidViewDefinition(defaultColumnOrder)}
          viewDefinitionComparator={viewDefinitionComparator(
            defaultColumnOrder,
          )}
          onViewCreate={onViewCreate}
          onViewDelete={onViewDelete}
          onViewUpdate={onViewUpdate}
          selectedViewId={currentViewId}
        />
        <StyledContainer>
          <GroupByMenu
            onChange={(latest) => setGroupKey(latest)}
            fields={fieldsForGroupBy}
            groupByKey={groupKey}
          />
        </StyledContainer>
        {groupKey !== 'none' && (
          <StyledRadioGroup value={mode} onChange={onCollapseChange}>
            {mode === 'expand' ? (
              <Radio.Button value="collapse">
                <Tooltip title={collapseAllTooltipText}>
                  <CollapseCategories16 />
                </Tooltip>
              </Radio.Button>
            ) : (
              <Radio.Button value="expand">
                <Tooltip title={expandAllTooltipText}>
                  <ExpandCategories16 />
                </Tooltip>
              </Radio.Button>
            )}
          </StyledRadioGroup>
        )}
      </FilterBarPrimary>
      <FilterBarSecondary>
        <ButtonWrapper>
          <SearchInput
            direction="right"
            searchButtonProps={{ type: 'text' }}
            placeholder="search"
            size="small"
            onChange={onSearchInputChange}
            value={searchValue}
            data-cy="projects.search"
            minWidth="280px"
          />
          <ColumnListPopover
            columnOrder={columnOrder}
            gridColumnApi={gridColumnApi}
            excludedColumns={['projectName']}
            defaultColumnOrder={
              defaultColumnOrder.length > 0
                ? defaultColumnOrder.map((x) => x.colId!)
                : []
            }
          />
          <Tooltip
            title={<FormattedMessage id="export.tooltip" />}
            placement="right"
          >
            <Button
              icon={
                <IconWrapper>
                  <Export16 />
                </IconWrapper>
              }
              type="text"
              size="small"
              loading={isExportInProgress}
              onClick={exportAsCsvWithToast}
              data-cy="projects.export"
            />
          </Tooltip>
        </ButtonWrapper>
        <ProjectCreateButton />
        {canShowGpt ? (
          <GptWrapper>
            <GptPage />
          </GptWrapper>
        ) : null}
      </FilterBarSecondary>
    </FilterBar>
  );
};
